import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Truck, Package, LogOut, Clock, CheckCircle, Send, Eye, Phone, Mail, MapPin, Calendar, Users, AlertCircle, PlayCircle, Flag, CreditCard, TrendingUp, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';
import QuoteFormModal from './QuoteFormModal';
import FleetPhotosManager from './FleetPhotosManager';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransporterDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [transporter, setTransporter] = useState(null);
  const [tripRequests, setTripRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('available'); // 'available', 'accepted', 'quotes'
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('transporter');
    if (!stored) {
      navigate('/drivers/login');
      return;
    }
    const transporterData = JSON.parse(stored);
    setTransporter(transporterData);
    fetchData(transporterData.user_id);
  }, [navigate]);

  const fetchData = async (userId) => {
    try {
      const requestsResponse = await axios.get(`${API}/trip-requests`);
      setTripRequests(requestsResponse.data);
      const quotesResponse = await axios.get(`${API}/quotes/transporter/${userId}`);
      setMyQuotes(quotesResponse.data);
    } catch (err) {
      // Error handled silently
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('transporter');
    navigate('/drivers/login');
  };

  const handleSendQuote = (request) => {
    setSelectedRequest(request);
    setIsQuoteModalOpen(true);
  };

  const handleQuoteSubmitted = (quoteData) => {
    // Refresh data after quote submission
    if (transporter) {
      fetchData(transporter.user_id);
    }
  };

  const handleConfirmBooking = async (quoteId) => {
    setUpdatingStatus(quoteId);
    try {
      await axios.post(`${API}/quotes/${quoteId}/confirm`, { transporter_user_id: transporter.user_id });
      await fetchData(transporter.user_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not confirm the booking. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleUpdateTripStatus = async (requestId, newStatus) => {
    setUpdatingStatus(requestId);
    try {
      await axios.patch(`${API}/trip-requests/${requestId}/status`, {
        status: newStatus,
        transporter_user_id: transporter.user_id
      });
      // Refresh data
      await fetchData(transporter.user_id);
    } catch (err) {
      // Error handled silently
    } finally {
      setUpdatingStatus(null);
    }
  };

  const hasQuotedRequest = (requestId) => {
    return myQuotes.some(quote => quote.trip_request_id === requestId);
  };

  const getQuoteForRequest = (requestId) => {
    return myQuotes.find(quote => quote.trip_request_id === requestId);
  };

  if (!transporter) {
    return null;
  }

  const pendingQuotesCount = myQuotes.filter(q => q.status === 'pending').length;
  // A booked quote = one that's been accepted by the customer, regardless of
  // whether the customer has paid yet. Once paid, the quote.status flips to
  // 'paid' (set server-side by the CMI reconciliation flow), but it's still
  // the same booking for the transporter — just with an extra "Paid" pill.
  const BOOKED_STATUSES = ['accepted', 'paid'];
  const acceptedQuotes = myQuotes.filter(q => BOOKED_STATUSES.includes(q.status));
  const acceptedQuotesCount = acceptedQuotes.length;
  const paidQuotesCount = myQuotes.filter(q => q.status === 'paid').length;

  // Helper: sort by created_at desc (newest first). Falls back to 0 if missing.
  const byNewest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

  // All quotes sorted newest-first for the "Quotes Sent" tab
  const sortedMyQuotes = [...myQuotes].sort(byNewest);

  // Get accepted trips with full details (sorted newest first)
  const acceptedTrips = acceptedQuotes.map(quote => {
    const tripRequest = tripRequests.find(req => req.request_id === quote.trip_request_id);
    return { ...quote, tripRequest };
  }).filter(item => item.tripRequest)
    .sort(byNewest);

  // Filter available requests (exclude ones where this transporter's quote was
  // accepted OR already paid by the customer) and sort by most recent first.
  const availableRequests = tripRequests.filter(req => {
    const quote = getQuoteForRequest(req.request_id);
    if (quote && BOOKED_STATUSES.includes(quote.status)) return false;
    return true;
  }).sort(byNewest);

  // === Earnings summary ===
  // Pulled from quote.transporter_earnings (the net amount the transporter
  // keeps after platform fees). We separate "received" (already-paid quotes)
  // from "pending" (accepted but not yet paid) and call out a this-month
  // sub-total to make the retention hook feel current.
  const now = new Date();
  const isThisMonth = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };
  const sumEarnings = (quotes) =>
    quotes.reduce((s, q) => s + (q.transporter_earnings || 0), 0);

  const paidQuotes = myQuotes.filter(q => q.status === 'paid');
  const pendingPaymentQuotes = myQuotes.filter(q => q.status === 'accepted');

  const earningsReceivedAllTime = sumEarnings(paidQuotes);
  const earningsReceivedThisMonth = sumEarnings(paidQuotes.filter(q => isThisMonth(q.created_at)));
  const earningsPending = sumEarnings(pendingPaymentQuotes);
  const formatMAD = (n) =>
    `MAD ${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <section className="pt-28 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {t('transporter.welcome')}, <span className="text-amber-500">{transporter.company_name}</span>!
              </h1>
              <p className="text-gray-400">{t('transporter.manageTrips')}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2">
                <p className="text-gray-400 text-xs">{t('transporter.userId')}</p>
                <p className="text-amber-500 font-mono font-bold">{transporter.user_id}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-xl"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout')}
              </Button>
            </div>
          </div>

          {/* Earnings Banner */}
          <div
            className="bg-gradient-to-br from-emerald-900/40 via-emerald-800/20 to-amber-900/20 border border-emerald-500/30 rounded-2xl p-6 mb-6 relative overflow-hidden"
            data-testid="earnings-banner"
          >
            <div className="absolute -right-12 -top-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider font-semibold mb-2">
                  <Wallet className="w-4 h-4" />
                  {t('transporter.earningsLabel') || 'Your Earnings'}
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <p className="text-4xl lg:text-5xl font-bold text-emerald-400" data-testid="earnings-month-amount">
                    {formatMAD(earningsReceivedThisMonth)}
                  </p>
                  <span className="text-gray-400 text-sm">
                    {t('transporter.receivedThisMonth') || 'received this month'}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  {t('transporter.allTime') || 'All-time'}:{' '}
                  <span className="text-emerald-300 font-mono font-semibold" data-testid="earnings-alltime-amount">
                    {formatMAD(earningsReceivedAllTime)}
                  </span>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 text-right">
                <div className="bg-black/30 border border-amber-500/20 rounded-xl p-4 min-w-[180px]">
                  <div className="flex items-center justify-end gap-2 text-amber-300 text-xs uppercase tracking-wider font-semibold mb-1">
                    <Clock className="w-3.5 h-3.5" />
                    {t('transporter.awaitingPayment') || 'Awaiting Payment'}
                  </div>
                  <p className="text-2xl font-bold text-amber-400 font-mono" data-testid="earnings-pending-amount">
                    {formatMAD(earningsPending)}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {pendingPaymentQuotes.length} {pendingPaymentQuotes.length === 1 ? 'booking' : 'bookings'}
                  </p>
                </div>
                <div className="bg-black/30 border border-emerald-500/20 rounded-xl p-4 min-w-[180px]">
                  <div className="flex items-center justify-end gap-2 text-emerald-300 text-xs uppercase tracking-wider font-semibold mb-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    {t('transporter.completed') || 'Completed'}
                  </div>
                  <p className="text-2xl font-bold text-emerald-400 font-mono">
                    {paidQuotes.length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {paidQuotes.length === 1 ? 'paid booking' : 'paid bookings'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-2xl p-6">
              <Package className="w-8 h-8 text-amber-500 mb-4" />
              <p className="text-3xl font-bold text-white">{availableRequests.length}</p>
              <p className="text-gray-400">{t('transporter.availableRequests')}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Send className="w-8 h-8 text-blue-500 mb-4" />
              <p className="text-3xl font-bold text-white">{myQuotes.length}</p>
              <p className="text-gray-400">{t('transporter.quoteSent')}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Clock className="w-8 h-8 text-yellow-500 mb-4" />
              <p className="text-3xl font-bold text-white">{pendingQuotesCount}</p>
              <p className="text-gray-400">{t('transporter.pendingResponse')}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/20 to-green-600/10 border border-emerald-500/30 rounded-2xl p-6">
              <CheckCircle className="w-8 h-8 text-emerald-400 mb-4" />
              <p className="text-3xl font-bold text-white">{acceptedQuotesCount}</p>
              <p className="text-gray-400">
                {t('transporter.activeBookings')}
                {paidQuotesCount > 0 && (
                  <span className="ml-2 text-emerald-400 text-xs font-semibold inline-flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    {paidQuotesCount} paid
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
            <Button
              onClick={() => setActiveTab('available')}
              variant={activeTab === 'available' ? 'default' : 'ghost'}
              className={`rounded-full px-6 ${
                activeTab === 'available' 
                  ? 'bg-amber-500 text-black hover:bg-amber-600' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="tab-available"
            >
              <Package className="w-4 h-4 mr-2" />
              {t('transporter.availableRequests')} ({availableRequests.length})
            </Button>
            <Button
              onClick={() => setActiveTab('accepted')}
              variant={activeTab === 'accepted' ? 'default' : 'ghost'}
              className={`rounded-full px-6 ${
                activeTab === 'accepted' 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="tab-accepted"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('transporter.myBookings')} ({acceptedQuotesCount})
            </Button>
            <Button
              onClick={() => setActiveTab('quotes')}
              variant={activeTab === 'quotes' ? 'default' : 'ghost'}
              className={`rounded-full px-6 ${
                activeTab === 'quotes' 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="tab-quotes"
            >
              <Send className="w-4 h-4 mr-2" />
              {t('transporter.allQuotes')} ({myQuotes.length})
            </Button>
            <Button
              onClick={() => setActiveTab('fleet')}
              variant={activeTab === 'fleet' ? 'default' : 'ghost'}
              className={`rounded-full px-6 ${
                activeTab === 'fleet'
                  ? 'bg-amber-500 text-black hover:bg-amber-600'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid="tab-fleet"
            >
              <Truck className="w-4 h-4 mr-2" />
              {t('transporter.fleetPhotos', 'Fleet Photos')}
            </Button>
          </div>

          {activeTab === 'fleet' && (
            <FleetPhotosManager userId={transporter.user_id} />
          )}

          {/* Available Requests Tab */}
          {activeTab === 'available' && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6">{t('transporter.availableRequests')}</h2>
              
              {availableRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">{t('transporter.noRequests')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableRequests.map((request) => {
                    const alreadyQuoted = hasQuotedRequest(request.request_id);
                    const existingQuote = getQuoteForRequest(request.request_id);
                    const isClosed = !['pending', 'quoted'].includes(request.status);
                    
                    return (
                      <div
                        key={request.id}
                        className={`bg-black/30 border rounded-xl p-6 transition-colors ${
                          isClosed
                            ? 'border-white/5 opacity-40 grayscale pointer-events-none select-none'
                            : alreadyQuoted 
                            ? 'border-blue-500/30 bg-blue-900/10' 
                            : 'border-white/5 hover:border-amber-500/30'
                        }`}
                        aria-disabled={isClosed}
                        data-closed={isClosed ? 'true' : 'false'}
                        data-testid={`request-card-${request.request_id}`}
                      >
                        <div className="flex flex-col lg:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                              <span className="bg-amber-500/20 text-amber-500 text-xs font-bold px-2 py-1 rounded">
                                {request.request_id}
                              </span>
                              <span className={`text-xs font-bold px-2 py-1 rounded ${
                                request.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                request.status === 'quoted' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {request.status.toUpperCase()}
                              </span>
                              {isClosed && (
                                <span className="text-xs font-bold px-2 py-1 rounded bg-gray-500/30 text-gray-300" data-testid={`closed-badge-${request.request_id}`}>
                                  AWARDED TO ANOTHER TRANSPORTER
                                </span>
                              )}
                              {alreadyQuoted && existingQuote && (
                                <span className={`text-xs font-bold px-2 py-1 rounded ${
                                  existingQuote.status === 'pending' 
                                    ? 'bg-blue-500/20 text-blue-500' 
                                    : (existingQuote.status === 'rejected' || existingQuote.status === 'lost')
                                    ? 'bg-red-500/20 text-red-500'
                                    : 'bg-green-500/20 text-green-500'
                                }`}>
                                  {existingQuote.status === 'pending' ? `${t('transporter.quoted')} MAD ${existingQuote.customer_total?.toFixed(2)}` : 
                                   (existingQuote.status === 'rejected' || existingQuote.status === 'lost') ? t('transporter.notSelected') : t('status.accepted')}
                                </span>
                              )}
                            </div>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <div className="flex items-start gap-2 mb-2">
                                  <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Pick-up</p>
                                    <p className="text-white">{request.pickup_address}</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <div className="w-3 h-3 rounded-full bg-amber-500 mt-1.5" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Drop-off</p>
                                    <p className="text-white">{request.dropoff_address}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Horses:</span>
                                  <span className="text-white">{request.horse_count}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Date:</span>
                                  <span className="text-white">{request.transport_date}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Contact:</span>
                                  <span className="text-white">{request.first_name} {request.last_name}</span>
                                </div>
                              </div>
                            </div>
                            
                            {request.additional_comments && (
                              <p className="text-gray-400 text-sm mt-3 p-3 bg-white/5 rounded-lg">
                                "{request.additional_comments}"
                              </p>
                            )}
                          </div>
                          
                          <div className="flex lg:flex-col gap-2">
                            {isClosed ? (
                              <Button variant="outline" disabled className="rounded-xl flex-1 border-gray-500/30 text-gray-400" data-testid={`closed-btn-${request.request_id}`}>
                                <AlertCircle className="w-4 h-4 mr-2" />
                                Closed
                              </Button>
                            ) : alreadyQuoted ? (
                              <Button
                                variant="outline"
                                className={`rounded-xl flex-1 ${
                                  (existingQuote?.status === 'rejected' || existingQuote?.status === 'lost')
                                    ? 'border-red-500/30 text-red-500' 
                                    : 'border-blue-500/30 text-blue-500'
                                }`}
                                disabled
                              >
                                {(existingQuote?.status === 'rejected' || existingQuote?.status === 'lost') ? (
                                  <>
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    {t('transporter.notSelected')}
                                  </>
                                ) : (
                                  <>
                                    <Clock className="w-4 h-4 mr-2" />
                                    {t('transporter.quotePending')}
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleSendQuote(request)}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl flex-1"
                                data-testid={`send-quote-btn-${request.request_id}`}
                              >
                                <Send className="w-4 h-4 mr-2" />
                                {t('transporter.sendQuote')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Accepted Bookings Tab */}
          {activeTab === 'accepted' && (
            <div className="bg-gradient-to-br from-green-900/20 to-green-800/10 border border-green-500/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Truck className="w-6 h-6 text-green-500" />
                {t('transporter.myBookings')}
              </h2>
              
              {acceptedTrips.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">{t('transporter.noBookings')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {acceptedTrips.map(({ tripRequest, ...quote }) => {
                    const isPaid = quote.status === 'paid';
                    const awaitingConfirm = quote.status === 'accepted' && tripRequest.status === 'accepted';
                    return (
                    <div
                      key={quote.id}
                      className={`bg-black/30 border rounded-xl p-6 ${
                        isPaid ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-green-500/20'
                      }`}
                      data-testid={`booking-card-${quote.quote_id}`}
                    >
                      <div className="flex flex-col lg:flex-row justify-between gap-6">
                        {/* Trip Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-4 flex-wrap">
                            {isPaid ? (
                              <span
                                className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1"
                                data-testid={`paid-badge-${quote.quote_id}`}
                              >
                                <CreditCard className="w-3 h-3" />
                                PAID
                              </span>
                            ) : awaitingConfirm ? (
                              <span className="bg-amber-500/20 text-amber-400 text-xs font-bold px-3 py-1 rounded-full" data-testid={`accepted-by-client-badge-${quote.quote_id}`}>
                                ACCEPTED BY CLIENT
                              </span>
                            ) : (
                              <span className="bg-green-500/20 text-green-500 text-xs font-bold px-3 py-1 rounded-full">
                                CONFIRMED
                              </span>
                            )}
                            <span className="bg-amber-500/20 text-amber-500 text-xs font-bold px-2 py-1 rounded font-mono">
                              {tripRequest.request_id}
                            </span>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              tripRequest.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                              tripRequest.status === 'confirmed' ? 'bg-blue-500/20 text-blue-500' :
                              tripRequest.status === 'in_progress' ? 'bg-purple-500/20 text-purple-500' :
                              tripRequest.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                              'bg-gray-500/20 text-gray-500'
                            }`}>
                              {awaitingConfirm ? 'AWAITING YOUR CONFIRMATION' : tripRequest.status.toUpperCase().replace('_', ' ')}
                            </span>
                          </div>

                          {/* Route */}
                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-gray-400 text-xs">Pick-up Location</p>
                                  <p className="text-white">{tripRequest.pickup_address}</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-gray-400 text-xs">Drop-off Location</p>
                                  <p className="text-white">{tripRequest.dropoff_address}</p>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                <div>
                                  <p className="text-gray-400 text-xs">Transport Date</p>
                                  <p className="text-white font-medium">{tripRequest.transport_date}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Users className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                <div>
                                  <p className="text-gray-400 text-xs">Number of Horses</p>
                                  <p className="text-white font-medium">{tripRequest.horse_count}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Customer Contact */}
                          <div className="bg-white/5 rounded-xl p-4 mb-4">
                            <p className="text-gray-400 text-sm mb-2">{t('transporter.customerContact')}</p>
                            <div className="flex flex-wrap gap-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                                  <Users className="w-4 h-4 text-amber-500" />
                                </div>
                                <span className="text-white font-medium">
                                  {tripRequest.first_name} {tripRequest.last_name}
                                </span>
                              </div>
                              <a 
                                href={`mailto:${tripRequest.email}`}
                                className="flex items-center gap-2 text-amber-500 hover:text-amber-400 transition-colors"
                              >
                                <Mail className="w-4 h-4" />
                                {tripRequest.email}
                              </a>
                              <a 
                                href={`tel:${tripRequest.contact_number}`}
                                className="flex items-center gap-2 text-amber-500 hover:text-amber-400 transition-colors"
                              >
                                <Phone className="w-4 h-4" />
                                {tripRequest.contact_number}
                              </a>
                            </div>
                          </div>

                          {/* Quote Notes */}
                          {quote.notes && (
                            <div className="bg-black/30 rounded-xl p-3">
                              <p className="text-gray-400 text-xs mb-1">{t('transporter.yourNotes')}</p>
                              <p className="text-white text-sm">{quote.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Price & Actions */}
                        <div className="lg:w-64 space-y-4">
                          <div className="bg-black/30 rounded-xl p-4">
                            <p className="text-gray-400 text-sm mb-2">{t('quote.quoteDetails')}</p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">{t('quote.transportPrice')}</span>
                                <span className="text-white font-mono">MAD {quote.transport_price?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">{t('quote.customerFee')}</span>
                                <span className="text-blue-400 font-mono">+MAD {quote.customer_fee?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">{t('quote.transporterFee')}</span>
                                <span className="text-red-400 font-mono">-MAD {quote.transporter_fee?.toFixed(2)}</span>
                              </div>
                              <div className="border-t border-white/10 pt-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">{t('quote.customerPays')}</span>
                                  <span className="text-amber-500 font-bold font-mono">MAD {quote.customer_total?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                  <span className="text-green-400 font-medium">{t('quote.youReceive')}</span>
                                  <span className="text-green-400 font-bold font-mono">MAD {quote.transporter_earnings?.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Status Actions */}
                          <div className="space-y-2">
                            {awaitingConfirm && (
                              <>
                                <p className="text-amber-300 text-xs text-center">The client accepted your offer. Confirm to let them pay.</p>
                                <Button
                                  onClick={() => handleConfirmBooking(quote.quote_id)}
                                  disabled={updatingStatus === quote.quote_id}
                                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl"
                                  data-testid={`confirm-booking-btn-${quote.quote_id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  {updatingStatus === quote.quote_id ? t('transporter.updating') : 'Confirm booking'}
                                </Button>
                              </>
                            )}
                            {tripRequest.status === 'confirmed' && (
                              <Button
                                onClick={() => handleUpdateTripStatus(tripRequest.request_id, 'in_progress')}
                                disabled={updatingStatus === tripRequest.request_id}
                                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-xl"
                                data-testid={`start-trip-btn-${tripRequest.request_id}`}
                              >
                                <PlayCircle className="w-4 h-4 mr-2" />
                                {updatingStatus === tripRequest.request_id ? t('transporter.updating') : t('transporter.startTrip')}
                              </Button>
                            )}
                            {tripRequest.status === 'in_progress' && (
                              <Button
                                onClick={() => handleUpdateTripStatus(tripRequest.request_id, 'completed')}
                                disabled={updatingStatus === tripRequest.request_id}
                                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl"
                                data-testid={`complete-trip-btn-${tripRequest.request_id}`}
                              >
                                <Flag className="w-4 h-4 mr-2" />
                                {updatingStatus === tripRequest.request_id ? t('transporter.updating') : t('transporter.markComplete')}
                              </Button>
                            )}
                            {tripRequest.status === 'completed' && (
                              <div className="flex items-center justify-center gap-2 text-green-400 font-semibold py-3 bg-green-500/10 rounded-xl">
                                <CheckCircle className="w-5 h-5" />
                                {t('transporter.tripCompleted')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          )}

          {/* All Quotes Tab */}
          {activeTab === 'quotes' && (
            <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/10 border border-blue-500/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">{t('transporter.allQuotes')}</h2>
              
              {myQuotes.length === 0 ? (
                <div className="text-center py-12">
                  <Send className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">{t('transporter.noQuotes')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-white/10">
                        <th className="pb-3 pr-4">Quote ID</th>
                        <th className="pb-3 pr-4">Trip</th>
                        <th className="pb-3 pr-4">Service</th>
                        <th className="pb-3 pr-4">Transport (Gross)</th>
                        <th className="pb-3 pr-4">Customer Pays</th>
                        <th className="pb-3 pr-4">You Receive</th>
                        <th className="pb-3 pr-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMyQuotes.map((quote) => (
                        <tr key={quote.id} className="border-b border-white/5">
                          <td className="py-3 pr-4">
                            <span className="text-amber-500 font-mono text-sm">{quote.quote_id}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-white text-sm">{quote.trip_request_id}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              quote.service_type === 'premium' ? 'bg-amber-500/20 text-amber-500' :
                              quote.service_type === 'express' ? 'bg-red-500/20 text-red-500' :
                              'bg-blue-500/20 text-blue-500'
                            }`}>
                              {quote.service_type.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-white font-mono">MAD {quote.transport_price?.toFixed(2)}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-amber-500 font-mono font-bold">MAD {quote.customer_total?.toFixed(2)}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-green-400 font-mono">MAD {quote.transporter_earnings?.toFixed(2)}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              quote.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                              quote.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                              quote.status === 'accepted' ? 'bg-green-500/20 text-green-500' :
                              (quote.status === 'rejected' || quote.status === 'lost') ? 'bg-red-500/20 text-red-500' :
                              'bg-gray-500/20 text-gray-500'
                            }`}>
                              {quote.status === 'lost' ? 'NOT SELECTED'
                                : (quote.status === 'accepted' && !quote.transporter_confirmed && tripRequests.find(r => r.request_id === quote.trip_request_id)?.status === 'accepted') ? 'ACCEPTED BY CLIENT'
                                : quote.status.toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Quote Form Modal */}
      <QuoteFormModal
        isOpen={isQuoteModalOpen}
        onClose={() => {
          setIsQuoteModalOpen(false);
          setSelectedRequest(null);
        }}
        tripRequest={selectedRequest}
        transporterUserId={transporter?.user_id}
        onQuoteSubmitted={handleQuoteSubmitted}
      />
    </div>
  );
};

export default TransporterDashboard;
